/**
 * Issues Dashboard Hook
 *
 * React Query hook for fetching aggregated issue statistics.
 * Used by IssuesOverview component in the Control Hub.
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

// ============================================================================
// Types
// ============================================================================

export interface IssuesSummary {
    total_issues: number;
    open_issues: number;
    critical_issues: number;
    blocking_issues: number;
}

export interface RecentIssue {
    issue_id: string;
    container_id: string;
    title: string;
    issue_type: string;
    issue_source: string | null;
    severity: "critical" | "high" | "medium" | "low";
    status: string;
    created_at: string | null;
}

export interface IssuesDashboardResponse {
    summary: IssuesSummary;
    by_status: Record<string, number>;
    by_severity: Record<string, number>;
    by_type: Record<string, number>;
    recent_issues: RecentIssue[];
    high_risk_containers: unknown[];
}

// ============================================================================
// Query Keys
// ============================================================================

export const ISSUES_DASHBOARD_QUERY_KEYS = {
    all: ["issues-dashboard"] as const,
    dashboard: (startDate?: string | null, endDate?: string | null) =>
        [...ISSUES_DASHBOARD_QUERY_KEYS.all, "dashboard", startDate, endDate] as const,
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to fetch aggregated issue statistics for dashboard display
 *
 * Returns summary counts, breakdowns by status/severity/type, and recent issues.
 */
export function useIssuesDashboard(
    startDate?: string | null,
    endDate?: string | null,
    enabled: boolean = true,
) {
    return useQuery({
        queryKey: ISSUES_DASHBOARD_QUERY_KEYS.dashboard(startDate, endDate),
        queryFn: async (): Promise<IssuesDashboardResponse> => {
            const params: Record<string, string> = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            return await apiGet<IssuesDashboardResponse>(
                "/container-issues/dashboard",
                { params },
            );
        },
        enabled,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
    });
}
